import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Brain, Compass, Users, BookOpen } from 'lucide-react';
import { SectionWrapper } from '../ui/SectionWrapper';
export function SolutionSection() {
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  const staggerContainer = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const features = [{
    icon: <Brain className="w-6 h-6 text-accent" data-id="element-170" />,
    title: 'DOMINIO DE MARCOS (FRAMES)',
    desc: 'Cómo establecer las reglas invisibles de la charla.'
  }, {
    icon: <Compass className="w-6 h-6 text-accent" data-id="element-171" />,
    title: 'GUÍA DE DECISIONES',
    desc: 'Cómo llevar a alguien del punto A al punto B sin que sienta presión.'
  }, {
    icon: <Users className="w-6 h-6 text-accent" data-id="element-172" />,
    title: 'PSICOLOGÍA DE LIDERAZGO INVISIBLE',
    desc: 'Estrategias para dominar grupos y reuniones.'
  }];
  const steps = [{
    num: '1',
    title: 'Establecés el Marco',
    desc: 'Tomás el control de la narrativa de la conversación.'
  }, {
    num: '2',
    title: 'Instalás la Idea',
    desc: 'Sembrás la propuesta usando lenguaje presuposicional.'
  }, {
    num: '3',
    title: 'Validás la Decisión',
    desc: "Dejás que el otro 'elija' lo que vos ya planeaste para él."
  }];
  return <SectionWrapper className="bg-surface border-y border-border relative overflow-hidden" data-id="element-173">
      {/* Subtle background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-accent/5 rounded-full blur-[120px] pointer-events-none" data-id="element-174" />

      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true,
      margin: '-100px'
    }} variants={staggerContainer} className="relative z-10" data-id="element-175">
        <div className="text-center mb-16" data-id="element-176">
          <motion.span variants={fadeUp} className="text-accent font-heading font-bold tracking-widest uppercase text-sm mb-4 block" data-id="element-177">
            El Mecanismo Único
          </motion.span>
          <motion.h2 variants={fadeUp} className="font-heading text-3xl md:text-5xl font-black text-text-primary uppercase tracking-tight mb-6" data-id="element-178">
            Cómo Instalar Ideas <br className="hidden md:block" data-id="element-179" /> Sin
            Resistencia
          </motion.h2>
          <motion.p variants={fadeUp} className="font-body text-lg text-text-secondary max-w-2xl mx-auto leading-relaxed" data-id="element-180">
            Este manual no te enseña a "pedir", te enseña a instalar. Utiliza
            mecanismos de psicología avanzada para sembrar una idea en la mente
            del otro de modo que, al florecer, crean que la idea fue de ellos.
            Es el arte de guiar decisiones sin confrontación, utilizando la
            propia arquitectura mental del interlocutor a tu favor.
          </motion.p>
        </div>

        {/* How It Works Steps */}
        <motion.div variants={fadeUp} className="mb-24" data-id="element-181">
          <div className="flex flex-col md:flex-row justify-between items-start relative" data-id="element-182">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-6 left-[10%] right-[10%] h-[1px] bg-border z-0" data-id="element-183" />

            {steps.map((step, idx) => <div key={idx} className="relative z-10 flex flex-col items-center text-center flex-1 mb-10 md:mb-0 px-4" data-id="element-184">
                <div className="w-12 h-12 rounded-full bg-bg border border-accent flex items-center justify-center text-accent font-heading font-bold text-xl mb-6 shadow-[0_0_15px_rgba(0,255,136,0.2)]" data-id="element-185">
                  {step.num}
                </div>
                <h4 className="font-heading font-bold text-text-primary mb-2" data-id="element-186">
                  {step.title}
                </h4>
                <p className="font-body text-sm text-text-secondary" data-id="element-187">
                  {step.desc}
                </p>
              </div>)}
          </div>
        </motion.div>

        {/* What You Get */}
        <div className="max-w-4xl mx-auto" data-id="element-188">
          <motion.div variants={fadeUp} className="mb-8" data-id="element-189">
            <h3 className="font-heading text-xl font-bold text-text-primary uppercase tracking-wider mb-6 text-center" data-id="element-190">
              Lo que obtenés
            </h3>
          </motion.div>

          <motion.div variants={fadeUp} className="bg-bg border border-border rounded-xl p-8 md:p-10 relative overflow-hidden group mb-6" data-id="element-191">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-accent glow-green" data-id="element-192" />
            <div className="flex flex-col md:flex-row items-center gap-8" data-id="element-193">
              <div className="w-48 md:w-56 shrink-0" data-id="element-194">
                <img src="https://cdn.magicpatterns.com/uploads/eZ4Nq5vkPKXydpYXUmX29m/heylloyd-image-1772481907061.png" alt="Arquitectura Mental de Influencia" className="w-full h-auto rounded-lg shadow-[0_0_40px_rgba(0,255,136,0.15)]" data-id="element-195" />
              </div>
              <div className="text-center md:text-left" data-id="element-196">
                <h4 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-text-primary mb-2 uppercase tracking-tight" data-id="element-197">
                  Manual Técnico: Arquitectura Mental de Influencia
                </h4>
                <p className="font-body text-accent font-medium tracking-wide" data-id="element-198">
                  Guías de aplicación directa
                </p>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-199">
            {features.map((feature, idx) => <motion.div key={idx} variants={fadeUp} className="bg-bg border border-border rounded-xl p-6 hover:border-accent/30 transition-colors" data-id="element-200">
                <div className="mb-4 p-3 bg-surface rounded-lg inline-block border border-border/50" data-id="element-201">
                  {feature.icon}
                </div>
                <h5 className="font-heading font-bold text-text-primary text-sm mb-3 uppercase tracking-wide leading-snug" data-id="element-202">
                  {feature.title}
                </h5>
                <p className="font-body text-sm text-text-secondary leading-relaxed" data-id="element-203">
                  {feature.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </motion.div>
    </SectionWrapper>;
}